import { sendMessage } from '../../features/realtime/realtime.slice';
import { RealtimeMessageType } from '../../features/realtime/realtime.types';
import {
  useAppDispatch,
  type DispatchReturnPromise,
  useAppSelector,
} from '../useReduxToolkit';
import { getUsername } from '../../features/realtime/realtime.selectors';
import { addRemovingParticipantTime } from '../../features/session/session.slice';
import { getAllMembersMap } from '../../features/realtime/realtime.selectors';
import { useHotpot } from '@doxyme/hotpot/react';

export type UseProviderActionsReturn = {
  /**
   * Removes one or more participants from the group call.
   */
  removeGroupCallParticipants: (ids: string[]) => DispatchReturnPromise;

  /**
   * Mutes a participant in the group call.
   */
  muteParticipant: (id: string) => DispatchReturnPromise;
  removeParticipantsAfterCallHangUp: (ids: string[]) => DispatchReturnPromise;
  removeOnHoldParticipants: (ids: string[]) => DispatchReturnPromise;
};

export const useProviderActions = (): UseProviderActionsReturn => {
  const dispatch = useAppDispatch();
  const username = useAppSelector(getUsername) ?? '';

  // for hotpot
  const hotpot = useHotpot();
  const membersMap = useAppSelector(getAllMembersMap);

  const removeGroupCallParticipants = async (
    ids: string[],
  ): DispatchReturnPromise => {
    return removeParticipants(
      ids,
      RealtimeMessageType.GroupCallParticipantLeft,
    );
  };

  const removeParticipants = async (
    ids: string[],
    messageType: RealtimeMessageType,
  ): DispatchReturnPromise => {
    dispatch(addRemovingParticipantTime(ids));

    return Promise.allSettled(
      ids.map(async (participantId) =>
        dispatch(
          sendMessage({
            id: participantId,
            message: {
              messageType,
            },
          }),
        ),
      ),
    ) as unknown as DispatchReturnPromise;
  };

  const removeParticipantsAfterCallHangUp = async (
    ids: string[],
  ): DispatchReturnPromise => {
    return removeParticipants(
      ids,
      RealtimeMessageType.RemoveParticipantsAfterCallHangUp,
    );
  };

  const removeOnHoldParticipants = async (
    ids: string[],
  ): DispatchReturnPromise => {
    // for hotpot
    void Promise.all(
      ids.map(async (id) => {
        const dbSessionId = membersMap[id]?.state?.dbSessionId;
        if (dbSessionId) {
          try {
            await hotpot?.visits.call.leaveByRealtimeId({
              realtimeId: id,
              sessionId: dbSessionId,
            });
            void hotpot?.visits.endCallByRealtimeId({
              realtimeId: id,
              sessionId: dbSessionId,
            });
          } catch (_error) {
            // error will be logged by hotpot
          }
        }
      }),
    );

    return removeParticipants(
      ids,
      RealtimeMessageType.RemoveOnHoldParticipants,
    );
  };

  const muteParticipant = async (id: string): DispatchReturnPromise => {
    return dispatch(
      sendMessage({
        id,
        message: {
          messageType: RealtimeMessageType.ParticipantMuted,
          payload: username,
        },
      }),
    );
  };

  return {
    removeGroupCallParticipants,
    muteParticipant,
    removeParticipantsAfterCallHangUp,
    removeOnHoldParticipants,
  };
};
